import { Spinner } from '@creditornot/cb-ingredients';
import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { Brand, Enterprise } from 'apis/enterprise/types';
import { DraftVenue } from 'apis/merchant-onboarding-service/types';
import { Merchant } from 'apis/restaurant-api/merchant-admin/merchant/types';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { setMetadataResource } from 'core/telemetry';
import { getBrandRoute } from 'modules/brand/utils/getBrandRoute';

import Result from './Result';
import VirtualizedList, { createResultSection, VirtualizedSection } from './VirtualizedList';
import { ResultType } from '../types';

const StyledLoader = styled.div`
  padding-block-start: 8px;
  padding-inline: 16px;
  display: flex;
  justify-content: center;
`;

const Loader = () => (
  <StyledLoader>
    <Spinner size="medium" />
  </StyledLoader>
);

const Root = styled.div`
  height: 444px;
`;

type Props = {
  brands: Brand[];
  enterprises: Enterprise[];
  draftVenues?: DraftVenue[];
  isLoading: boolean;
  merchants: Merchant[];
  venues: Venue[];
};

const SearchResults = ({ brands, enterprises, draftVenues = [], isLoading, merchants, venues }: Props) => {
  const history = useHistory();

  const navigate = (slug: string, resultType: ResultType, resultId: string = '') => {
    resultId = resultId || slug;
    setMetadataResource(resultId, resultType);
    history.push(`/${resultType.replace('_', '-')}/${slug}`);
  };

  const enterpriseResults = enterprises.map(enterprise => {
    const resultType = 'enterprise';

    return <Result key={enterprise.id} label={enterprise.name} onClick={() => navigate(enterprise.id, resultType)} />;
  });

  const brandResults = brands.map(brand => {
    const brandUrl = getBrandRoute('purchase-data', brand.enterpriseId, brand.id);
    return <Result key={brand.id} label={brand.name} subLabel={brand.country} onClick={() => history.push(brandUrl)} />;
  });

  const merchantResults = merchants.map(merchant => {
    const slug = merchant.itemid.$oid || '';
    const resultType = 'merchant';

    return <Result key={slug} label={merchant.name[0].value} onClick={() => navigate(slug, resultType)} />;
  });

  const draftVenueResults = draftVenues.map(venue => (
    <Result key={venue.id} label={venue.name} subLabel={venue.address} tag="PENDING APPROVAL" disabled />
  ));

  const venueResults = venues.map(venue => {
    const slug = venue.slug || '';
    const resultType = 'venue';
    const resultId = venue.id;
    const listImage = venue.listimage || undefined;

    return (
      <Result
        key={slug}
        label={venue.name[0].value}
        onClick={() => navigate(slug, resultType, resultId)}
        subLabel={venue.address}
        listImage={listImage}
      />
    );
  });

  const shouldShowResults =
    !!merchants.length || !!draftVenues.length || !!venues.length || !!enterprises.length || !!brands.length;

  const virtualizedSections = useMemo((): VirtualizedSection[] => {
    const sections = [
      createResultSection(enterpriseResults, 'merchantSearch.enterprises'),
      createResultSection(brandResults, 'merchantSearch.brands'),
      createResultSection(merchantResults, 'merchantSearch.merchants'),
      createResultSection([...draftVenueResults, ...venueResults], 'merchantSearch.venues'),
    ];

    return sections.filter((section): section is VirtualizedSection => !!section);
  }, [brandResults, draftVenueResults, enterpriseResults, merchantResults, venueResults]);

  return (
    <Root>
      {isLoading && <Loader />}
      {!isLoading && shouldShowResults && <VirtualizedList sections={virtualizedSections} />}
    </Root>
  );
};

export default SearchResults;
